import type { ComponentProps, ReactElement } from 'react';
import { Children, cloneElement, Fragment, isValidElement } from 'react';

import type { ButtonProps } from './Button';
import type { ColorName, ColorVariant } from './buttonClasses';
import { composeVariant, segmentDividerVariantClasses, segmentVariantClasses } from './buttonClasses';
import type { Style } from './colors';
import type { Shape, Size } from './types';
import { cn } from '../cn';
import { childRoundedClasses, shapeRounded, sizeHeight } from './types';

type ButtonSegmentProps<ID> = Omit<ComponentProps<'div'>, 'onChange'> & {
  size?: Size;
  disabled?: boolean;
  shape?: Shape;
  equalWidth?: boolean;
  selectedId?: ID;
  onChange: (id: ID) => void;
  color?: ColorName;
  tone?: Style;
};

export default function ButtonSegment<ID>({
  children,
  className,
  disabled,
  shape,
  equalWidth = false,
  selectedId,
  onChange,
  size = 'md',
  color,
  tone,
}: ButtonSegmentProps<ID>) {
  const effectiveColor: ColorName = color ?? 'gray';
  const effectiveTone: Style = tone ?? 'filled';
  const effectiveVariant: ColorVariant = composeVariant(effectiveColor, effectiveTone);
  const filtered: ReactElement<ButtonProps & { id: ID }>[] = [];
  Children.forEach(children, (child) => {
    if (child != null && isValidElement<ButtonProps & { id: ID }>(child)) {
      filtered.push(child);
    }
  });

  if (filtered.length === 0) return null;

  return (
    <div
      className={cn(
        'inline-flex overflow-hidden border',
        segmentVariantClasses(effectiveVariant),
        sizeHeight(size),
        shapeRounded(shape, size),
        className,
      )}>
      {filtered.map((child, i) => (
        <Fragment key={i}>
          {i > 0 && <div className={cn('h-full w-px', segmentDividerVariantClasses(effectiveVariant))} />}
          {cloneElement(child, {
            size,
            bordered: false,
            active: child.props.id === selectedId,
            disabled: child.props.disabled ?? disabled,
            // A child that sets its own color keeps its color+tone; otherwise it inherits the segment's.
            ...(child.props.color == null ? { color: effectiveColor, tone: effectiveTone } : {}),
            onClick: () => onChange(child.props.id),
            className: cn(
              'h-full',
              equalWidth && 'flex-1 min-w-0',
              childRoundedClasses(i === 0, i === filtered.length - 1, shape),
              child.props.className,
            ),
          })}
        </Fragment>
      ))}
    </div>
  );
}
